<template>
	<CardGeneric class="container flex mx-auto max-sm:pb-6 productCardColors productCardComponent">
		<div class="grid grid-row-2 w-full">
			<div class="row-span-1 grid gap-4">
				<div class="flex flex-col sm:flex-row justify-between">
					<div class="flex flex-col md:flex-row grow gap-2 lg:gap-4">
						<img
							v-if="showProductImages"
							class="rounded-xl h-80 sm:h-32 w-full sm:w-32 object-cover shrink-0"
							:class="{ 'cursor-pointer hover:opacity-70 hoverEffects': hasMoreThanOneImage }"
							width="300"
							height="300"
							loading="lazy"
							:alt="`${title}`"
							:src="images.length ? images[0] : runtimeConfig.public.DEFAULT_PRODUCT_IMAGE"
							@click="hasMoreThanOneImage && imageModal.openModal()" />

						<div v-if="showProductImages">
							<LazyProductImageCarousel
								v-if="hasMoreThanOneImage"
								ref="imageModal"
								:ticket-name="title"
								:image-list="images.length ? images : [runtimeConfig.public.DEFAULT_PRODUCT_IMAGE]" />
						</div>

						<div
							class="text-left flex flex-col justify-between place-content-center gap-2"
							:class="{ '!justify-evenly': !shortDescription }">
							<p class="font-serif text-primary text-2xl lg:text-4xl mt-2 customCardTitle">{{ title }}</p>
							<p
								class="font-serif text-primary text-sm lg:text-base customCardTitle"
								v-if="shortDescription"
								v-html="shortDescription"></p>
							<div class="flex justify-between">
								<div class="flex">
									<p class="font-serif text-ternary text-4xl">
										{{ currency.symbol }}{{ numberHelper.formatPrice(price[currency.code]) }}
									</p>

									<p class="text-ternary text-xs font-light mt-2 ml-1">{{ currency.code }}</p>
								</div>

								<div>
									<div class="md:hidden text-right mt-2">
										<ButtonIconThenText
											v-if="description.length"
											class="text-ternary text-xs hoverEffects viewDetails"
											:text="showDescription ? $t('hideDetails') : $t('viewDetails')"
											icon="fa-solid fa-circle-info"
											@clicked="expandDetails" />
									</div>
								</div>
							</div>
						</div>
					</div>

					<div class="flex flex-col justify-between max-md:mt-2 md:pt-1 sm:basis-40 lg:basis-80 shrink-0">
						<div
							v-if="isOnAddonPage && !lodgingCount"
							class="text-ternary text-right italic text-sm">
							{{ $t("noLodgingItem") }}
						</div>

						<div v-else>
							<div
								v-if="availableQuantity > 0"
								class="flex flex-wrap lg:flex-nowrap justify-between sm:justify-end gap-3 lg:gap-4">
								<ButtonPillCounter
									:id="id + 'counter'"
									:key="amountInCart()"
									:initial-value="count"
									:max="availableQuantity - amountInCart()"
									@changed="countChanged" />
								<ButtonPrimary
									:id="id + '-btn'"
									class="add-to-cart-btn"
									:disabled="count < 1"
									@clicked="addToCart">
									{{ addToCartBtnText }}
								</ButtonPrimary>
							</div>

							<div
								v-else-if="isQuantityInCarts === true"
								class="font-serif text-3xl text-primary text-right">
								<span>{{ $t("unavailable") }}</span>
								<FormTooltip
									:id="id + '-unavailable-tooltip'"
									class="font-sans text-sm text-left top-[-10px]"
									:tooltipMsg="$t('soldOutTooltip')" />
							</div>

							<div
								v-else
								class="font-serif text-3xl text-primary text-right flex flex-col gap-2">
								<span>{{ $t("soldOut") }}</span>
								<NuxtLink
									v-if="soldOutLink"
									:to="soldOutLink"
									class="block text-ternary text-xs font-sans font-thin underline hover:text-primary w-fit ml-auto"
									target="_blank"
									rel="noopener noreferrer"
									>{{ $t("soldOutLink1") }}
								</NuxtLink>

								<NuxtLink
									v-if="allProductsSoldOut && allProductsSoldOutLink"
									:to="allProductsSoldOutLink"
									class="block text-ternary text-xs font-sans font-thin underline hover:text-primary w-fit ml-auto"
									target="_blank"
									rel="noopener noreferrer"
									>{{ $t("soldOutLink2") }}
								</NuxtLink>
							</div>
						</div>

						<div class="hidden-mobile-block text-right mb-1">
							<ButtonIconThenText
								v-if="description.length"
								class="text-ternary text-xs hoverEffects viewDetails"
								:text="showDescription ? $t('hideDetails') : $t('viewDetails')"
								icon="fa-solid fa-circle-info"
								@clicked="expandDetails" />
						</div>
					</div>
				</div>
			</div>

			<transition name="slide-fade">
				<div
					v-if="showDescription"
					class="flex gap-2 lg:gap-4 pt-4">
					<p
						class="productDescription text-ternary text-left sm:ml-36 lg:ml-40"
						:class="{ '!ml-0': !showProductImages }"
						v-html="description"></p>
				</div>
			</transition>
		</div>
	</CardGeneric>
</template>

<script setup>
	import { useCurrencyStore } from "~/stores/CurrencyStore"
	import { storeToRefs } from "pinia"
	import { useNuxtApp } from "#app"
	import { useCartStore } from "~/stores/CartStore"
	import { RESET_PRODUCT_CARD, QTY_ADDED_TO_CART } from "assets/events/api.js"
	import numberHelper from "assets/helpers/numberHelper.js"

	const { $cartService, $eventBus } = useNuxtApp()
	const runtimeConfig = useRuntimeConfig()
	const currencyStore = useCurrencyStore()
	const { currency } = storeToRefs(currencyStore)

	const { id, images } = defineProps({
		id: String,
		title: String,
		description: String,
		shortDescription: {
			type: String,
			required: false,
		},
		images: Array,
		price: Object,
		availableQuantity: Number,
		soldOutLink: {
			type: String,
			default: "",
		},
		allProductsSoldOut: Boolean,
		allProductsSoldOutLink: {
			type: String,
			default: "",
		},
		isQuantityInCarts: Boolean,
	})

	const { t } = useI18n()
	const imageModal = ref(null)
	const count = ref(1)
	const addToCartBtnText = ref(t("addToCart"))
	const showDescription = ref(false)
	const hasMoreThanOneImage = images ? images.length > 1 : false
	const showProductImages = runtimeConfig.public.SHOW_PRODUCT_IMAGES

	const countChanged = (val) => {
		if (val) {
			addToCartBtnText.value = t("addToCart")
		}
		count.value = val
	}

	const addToCart = () => {
		//Look at handler for QTY_ADDED_TO_CART event for success handler after API call
		$cartService.$addCartItemQty(id, count.value)
	}

	const expandDetails = () => {
		showDescription.value = !showDescription.value
	}

	//Add-on page specific code - Do not allow addons to be added without lodging item in cart
	const cartStore = useCartStore()
	const { items } = storeToRefs(cartStore)
	const lodgingCount = ref(0)
	const path = useRoute().path

	let isOnAddonPage = false
	if (path.indexOf("/products/addons") >= 0) {
		isOnAddonPage = true
	}
	if (isOnAddonPage) {
		const lodgingProductTypeId = runtimeConfig.public.PRODUCTS.lodging.productTypeId
		watch(
			items,
			() => {
				lodgingCount.value = cartStore.getNumProductsByType(lodgingProductTypeId)
			},
			{ deep: true }
		)
		lodgingCount.value = cartStore.getNumProductsByType(lodgingProductTypeId)
	}
	//End Add-on page specific code

	const amountInCart = () => {
		const itemInCart = cartStore.getItem(id)
		return itemInCart ? itemInCart.quantity : 0
	}

	onMounted(() => {
		$eventBus.$on(RESET_PRODUCT_CARD, () => {
			addToCartBtnText.value = t("addToCart")
			count.value = 1
		})

		$eventBus.$on(QTY_ADDED_TO_CART, (data) => {
			//Only trigger the listener if it was this component that fired it
			if (data.productId === id) {
				addToCartBtnText.value = t("addedToCart")
				count.value = 0
			}
		})
	})

	onUnmounted(() => {
		$eventBus.$off(RESET_PRODUCT_CARD)
		$eventBus.$off(QTY_ADDED_TO_CART)
	})
</script>

<style lang="scss" scoped>
	.add-to-cart-btn {
		width: 9.3rem;
	}
</style>

<style lang="scss">
	.productDescription ul {
		@apply list-disc ml-8;
	}
</style>
